import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import BaseComponent from '../../../baseClasses/BaseComponent';
import { userHasInsightsOnlyRole } from '../../../helpers/user/insightsRoleAccess';
import './InsightsDashboard.scss';

const BAKED_INSIGHTS_URL = process.env.REACT_APP_INSIGHTS_DASHBOARD_URL || '';

/** Must match the baked production Insights URL so the runtime override only runs when needed. */
const PRODUCTION_INSIGHTS_ORIGIN = 'https://insights.activeoversight.io';

/** Non-prod envs that get a runtime URL override. Add new envs (e.g. uat) here and in the hostname regex. */
const ALLOWED_INSIGHTS_ENVS = ['qa', 'staging', 'dev', 'cd'];
const HOSTNAME_REGEX = new RegExp(
  '^ao-(' + ALLOWED_INSIGHTS_ENVS.join('|') + ')\\.(finalze\\.com|activeoversight\\.io)$',
  'i'
);

/**
 * Use baked env URL unless it's production and we're on a known non-prod host (e.g. ao-qa.finalze.com),
 * in which case use the matching Insights env URL so the iframe loads without redeploy.
 */
function getInsightsDashboardUrl() {
  const baked = (BAKED_INSIGHTS_URL || '').trim();
  if (!baked) return '';
  try {
    const origin = new URL(baked).origin;
    if (origin !== PRODUCTION_INSIGHTS_ORIGIN) return baked;
  } catch (e) {
    return baked;
  }
  const hostname =
    typeof window !== 'undefined' && window.location && window.location.hostname
      ? window.location.hostname
      : '';
  const match = hostname.match(HOSTNAME_REGEX);
  if (match) {
    const env = match[1].toLowerCase();
    return 'https://insights-' + env + '.activeoversight.io/';
  }
  return baked;
}

const INSIGHTS_DASHBOARD_URL = getInsightsDashboardUrl();

/**
 * Query flag for the Insights SPA to hide chrome when embedded in AO (Insights app must honor it).
 * Not a security boundary: anyone can add this query param; authorization remains token/API-side.
 */
export const INSIGHTS_AO_EMBED_PARAM = 'ao_embed';

/** Must match the embedded Insights app (postMessage type). */
export const INSIGHTS_NAVIGATE_MESSAGE_TYPE = 'INSIGHTS_NAVIGATE';

function isSafeInternalPath(path) {
  return (
    typeof path === 'string' &&
    path.length > 0 &&
    path.startsWith('/') &&
    !path.startsWith('//')
  );
}

function buildInsightsIframeSrc(baseUrl, embedInAoShell) {
  const trimmed = (baseUrl || '').trim();
  if (!trimmed) {
    return '';
  }
  if (!embedInAoShell) {
    return trimmed;
  }
  try {
    const u = new URL(trimmed);
    u.searchParams.set(INSIGHTS_AO_EMBED_PARAM, '1');
    return u.toString();
  }
  catch (e) {
    const sep = trimmed.includes('?') ? '&' : '?';
    return `${trimmed}${sep}${INSIGHTS_AO_EMBED_PARAM}=1`;
  }
}

function getInsightsOrigin() {
  if (!INSIGHTS_DASHBOARD_URL) return null;
  try {
    return new URL(INSIGHTS_DASHBOARD_URL).origin;
  } catch (e) {
    return null;
  }
}

/**
 * Send message to iframe; tries strict targetOrigin first, then '*' to avoid throw when iframe
 * origin is null (e.g. failed to load). Logs a warning in development if both attempts fail.
 */
function sendPostMessage(iframe, message, targetOrigin) {
  try {
    iframe.contentWindow.postMessage(message, targetOrigin);
    return;
  } catch (e) {
    if (targetOrigin === '*') throw e;
  }
  try {
    iframe.contentWindow.postMessage(message, '*');
  } catch (e2) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[InsightsDashboard] postMessage failed (iframe may have origin null). Ensure REACT_APP_INSIGHTS_DASHBOARD_URL matches this environment.'
      );
    }
  }
}

export class InsightsDashboard extends BaseComponent {
  constructor(props) {
    super(props);
    this.insightsIframeRef = React.createRef();
    this.sendInsightsAuth = this.sendInsightsAuth.bind(this);
  }

  componentDidMount() {
    super.componentDidMount();
    window.addEventListener('message', this.handleInsightsNavigateMessage);
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleInsightsNavigateMessage);
    super.componentWillUnmount();
  }

  handleInsightsNavigateMessage(event) {
    if (!event || !event.data || event.data.type !== INSIGHTS_NAVIGATE_MESSAGE_TYPE) {
      return;
    }
    const allowedOrigin = getInsightsOrigin();
    if (!allowedOrigin || event.origin !== allowedOrigin) {
      return;
    }
    const path = event.data.path;
    if (!isSafeInternalPath(path) || !this.props.history) {
      return;
    }
    this.props.history.push(path);
  }

  sendInsightsAuth() {
    const iframe = this.insightsIframeRef && this.insightsIframeRef.current;
    if (!iframe || !iframe.contentWindow) return;

    const jwt = localStorage.token;

    if (!jwt || typeof jwt !== 'string') return;

    const companyId = this.props.currentCompany && this.props.currentCompany.id != null
      ? String(this.props.currentCompany.id)
      : (localStorage.current_company_id != null ? String(localStorage.current_company_id) : '');

    const message = { type: 'INSIGHTS_AUTH', payload: { token: jwt, companyId } };
    const targetOrigin = getInsightsOrigin() || '*';
    sendPostMessage(iframe, message, targetOrigin);
  }

  componentDidUpdate(prevProps) {
    if (INSIGHTS_DASHBOARD_URL && (prevProps.currentCompany !== this.props.currentCompany || prevProps.currentUser !== this.props.currentUser)) {
      this.sendInsightsAuth();
    }
  }

  render() {
    if (!INSIGHTS_DASHBOARD_URL) {
      return (
        <div className="insights-dashboard insights-dashboard--empty">
          <p>Configure REACT_APP_INSIGHTS_DASHBOARD_URL to show the insights dashboard.</p>
        </div>
      );
    }

    const embedChromeHidden =
      userHasInsightsOnlyRole(this.props.currentUser) ||
      !!(this.props.permissions && this.props.permissions.isInsightsOnlyUser);
    const iframeSrc = buildInsightsIframeSrc(INSIGHTS_DASHBOARD_URL, embedChromeHidden);

    return (
      <div className="insights-dashboard">
        <iframe
          ref={this.insightsIframeRef}
          id="insights-iframe"
          title="AO Dashboard Insights"
          src={iframeSrc}
          className="insights-dashboard-iframe"
          onLoad={this.sendInsightsAuth}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  currentUser: state.new_users.currentUser,
  currentCompany: state.new_companies.currentCompany,
  permissions: state.new_users.permissions,
});

export default withRouter(connect(mapStateToProps)(InsightsDashboard));



// WEBPACK FOOTER //
// ./src/containers/ManagerDashboard/InsightsDashboard/index.js